import styled from 'styled-components';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { RhythmInput } from '../../../../../common/src/rhythm_echo.ts';
import { PrimaryButton, TextButton } from '../learn_buttons.tsx';
import { LevelChips } from '../learn_level_chips.tsx';
import { ScreenBody, ScreenSubtitle, ScreenTitle, Slack } from './rhythm_screen_parts.tsx';

export const rhythmStartScreenId = 'rhythm-start';
export const rhythmStartButtonId = 'rhythm-start-button';
export const rhythmCalibrateButtonId = 'rhythm-calibrate-button';

// The level chips and their unlock caption are shared with the other exercises (issues #3473,
// #3518); re-exported here so the rhythm screens and stories keep reading them from this file
export { learnLevelChipTestId as rhythmLevelChipTestId, unlockCaption } from '../learn_level_chips.tsx';

// The start screen (issue #3473, not on the canvas): a line about the exercise, the shared level
// chips, Start and the calibration link. The Start tap is the user gesture that starts audio, so it
// is a real button and not automatic.
export const RhythmStartScreen = ({
  level,
  unlocked,
  selectable,
  calibrated,
  justCalibrated = false,
  onSelectLevel,
  onStart,
  onCalibrate,
  input = 'tap',
}: {
  input?: RhythmInput; // 'voice' is Sing Rhythm's start screen (issue #3723): the same, saying sing
  level: LearnLevel;
  unlocked: LearnLevel[];
  selectable: LearnLevel[];
  calibrated: boolean; // an offset is stored on this device
  justCalibrated?: boolean; // a calibration finished since the screen was last shown
  onSelectLevel: (level: LearnLevel) => void;
  onStart: () => void;
  onCalibrate: () => void;
}) => (
  <ScreenBody id={rhythmStartScreenId} data-input={input}>
    <ScreenTitle>{startTitle(input)}</ScreenTitle>
    <ScreenSubtitle>{startSubtitle(input)}</ScreenSubtitle>
    <LevelChips level={level} unlocked={unlocked} selectable={selectable} onSelectLevel={onSelectLevel} />
    <Slack />
    <PrimaryButton id={rhythmStartButtonId} data-testid={rhythmStartButtonId} onClick={onStart}>
      Start
    </PrimaryButton>
    <CalibrateRow>
      <TextButton id={rhythmCalibrateButtonId} data-testid={rhythmCalibrateButtonId} onClick={onCalibrate}>
        {calibrated ? 'Recalibrate' : 'Calibrate'}
      </TextButton>
      <CalibrateNote>{calibrationNote(calibrated, justCalibrated, input)}</CalibrateNote>
    </CalibrateRow>
  </ScreenBody>
);

export function startTitle(input: RhythmInput = 'tap'): string {
  return input === 'voice' ? 'Hear it, then sing it back' : 'Hear it, then tap it back';
}

export function startSubtitle(input: RhythmInput = 'tap'): string {
  return input === 'voice'
    ? 'Listen, then sing it back on one note. Any note: only the rhythm counts.'
    : 'Listen, then tap it back on the big button.';
}

function calibrationNote(calibrated: boolean, justCalibrated: boolean, input: RhythmInput): string {
  if (justCalibrated) {
    return 'Calibrated';
  }
  if (!calibrated) {
    return 'Not yet calibrated';
  }
  return input === 'voice' ? 'Your voice is matched to this device' : 'Taps are matched to this device';
}

const CalibrateRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
`;

const CalibrateNote = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;
